import {
  ReactFlow,
  type NodeTypes,
  type ReactFlowInstance,
  type Rect,
} from '@xyflow/react';
import { useCallback, useMemo } from 'react';

import {
  ChartCaptureGate,
  ChartMinimap,
  ChartViewportSync,
  ReadableViewportHelper,
} from '../../ChartViewport';
import type { StructureChartAdapter } from '../adapters';
import { StructureCanvasHelper, type StructureEntityFlowNode } from '../canvas';
import {
  useEntityActivation,
  useFocusEntity,
  useRelationHighlight,
  useRevealOnFocus,
  useStructureCanvasViewport,
} from '../hooks';
import type { LayoutResult } from '../layout';
import type { Relation } from '../model';
import { FocusedEntityContext } from './FocusedEntityContext';

import { LinesAndLabelsLayer } from './LinesAndLabelsLayer';
import { StructureDotGrid } from './StructureDotGrid';
import { StructureExportMenu } from './StructureExportMenu';
import { StructureLegend } from './StructureLegend';
import { StructureZoomControls } from './StructureZoomControls';

interface StructureCanvasProps {
  adapter: Readonly<StructureChartAdapter>;
  nodeSize: Readonly<{ width: number; height: number }>;
  layout: Readonly<LayoutResult>;
  relations: ReadonlyArray<Relation>;
  flowNodes: StructureEntityFlowNode[];
  companyId: number;
  companyName?: string;
  onEntityClick?: (entityId: number) => void;
  onUnlinkedClick?: (entityId: number) => void;
  hideControls?: boolean;
  onReady?: (rf: ReactFlowInstance, contentBounds: Rect) => void;
}

// React Flow's stylesheet removes the outline from focused nodes.
const focusRingCss = `
.react-flow .react-flow__node-${StructureCanvasHelper.NODE_TYPE} {
  border-radius: var(--pf-radius-full);
}
.react-flow .react-flow__node-${StructureCanvasHelper.NODE_TYPE}:focus-visible,
.react-flow .${StructureCanvasHelper.CHIP_CLASS}:focus-visible {
  outline: 2px solid var(--pf-color-blue-700);
  outline-offset: 2px;
}
`;

// Must render inside <ReactFlowProvider>.
const StructureCanvas = ({
  adapter,
  nodeSize,
  layout,
  relations,
  flowNodes,
  companyId,
  companyName,
  onEntityClick,
  onUnlinkedClick,
  hideControls,
  onReady,
}: StructureCanvasProps) => {
  const nodeTypes = useMemo<NodeTypes>(
    () => ({ [StructureCanvasHelper.NODE_TYPE]: adapter.NodeComponent }),
    [adapter]
  );
  const translateExtent = useStructureCanvasViewport(layout.bounds);
  const { highlightedRelationIds, handleNodeMouseEnter, handleNodeMouseLeave } =
    useRelationHighlight(relations);
  const { focusedEntityId, focusEntity } = useFocusEntity(
    layout.nodes,
    adapter.minZoom
  );
  const { handleNodeClick, handleKeyDown } = useEntityActivation(
    flowNodes,
    onEntityClick,
    onUnlinkedClick
  );
  const revealChip = useRevealOnFocus();
  const handleReady = useCallback(
    (rf: ReactFlowInstance) => onReady?.(rf, layout.bounds),
    [onReady, layout.bounds]
  );

  return (
    <FocusedEntityContext.Provider value={focusedEntityId}>
      <style>{focusRingCss}</style>
      <ChartViewportSync
        nodes={flowNodes}
        nodeSize={nodeSize}
        minZoom={adapter.minZoom}
        contentBounds={layout.bounds}
      />
      <ReactFlow<StructureEntityFlowNode>
        nodes={flowNodes}
        nodeTypes={nodeTypes}
        translateExtent={translateExtent}
        minZoom={ReadableViewportHelper.MANUAL_MIN_ZOOM}
        maxZoom={ReadableViewportHelper.MAX_ZOOM}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={Boolean(onEntityClick ?? onUnlinkedClick)}
        // Wheel pans; pinch and Cmd/Ctrl+scroll zoom, as in Figma.
        panOnScroll
        panOnScrollSpeed={1}
        proOptions={{ hideAttribution: true }}
        aria-label="Entity structure chart"
        onKeyDown={handleKeyDown}
        onNodeClick={handleNodeClick}
        onNodeMouseEnter={handleNodeMouseEnter}
        onNodeMouseLeave={handleNodeMouseLeave}
        style={{ background: 'transparent' }}
      >
        <StructureDotGrid />
        <LinesAndLabelsLayer
          layout={layout}
          highlightedRelationIds={highlightedRelationIds}
          onChipSelect={focusEntity}
          onChipReveal={revealChip}
        />
      </ReactFlow>
      {!hideControls && (
        <>
          <ChartMinimap
            nodes={flowNodes}
            nodeSize={nodeSize}
            contentBounds={layout.bounds}
          />
          <StructureLegend
            lines={layout.lines}
            section={adapter.legendSection}
          />
          <StructureZoomControls
            flowNodes={flowNodes}
            contentBounds={layout.bounds}
            nodeSize={nodeSize}
            minZoom={adapter.minZoom}
          />
          <StructureExportMenu
            companyId={companyId}
            companyName={companyName}
            contentBounds={layout.bounds}
            exporter={adapter}
          />
        </>
      )}
      {onReady && <ChartCaptureGate onReady={handleReady} />}
    </FocusedEntityContext.Provider>
  );
};

export { StructureCanvas };
